import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface Photo {
  id: string;
  projectId: string;
  dailyLogId: string | null;
  costCodeId: string | null;
  url: string;
  thumbnailUrl: string | null;
  caption: string | null;
  takenAt: string;
  locationLat: number | null;
  locationLng: number | null;
  createdBy: string | null;
  createdAt: string;
}

export interface UploadPhotoData {
  file: File;
  metadata?: {
    dailyLogId?: string;
    costCodeId?: string;
    caption?: string;
    takenAt?: string;
    locationLat?: number;
    locationLng?: number;
  };
}

export interface UpdatePhotoData {
  caption?: string;
  dailyLogId?: string;
  costCodeId?: string;
}

interface PhotosResponse {
  photos: Photo[];
}

interface PhotoResponse {
  photo: Photo;
}

// Get all photos for a project
export function usePhotos(projectId: string, dailyLogId?: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "photos", dailyLogId || "all"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const url = dailyLogId
        ? `/api/projects/${projectId}/photos?dailyLogId=${dailyLogId}`
        : `/api/projects/${projectId}/photos`;

      return fetchApi<PhotosResponse>(url, { headers });
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// Get a single photo
export function usePhoto(projectId: string, photoId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "photos", photoId],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<PhotoResponse>(`/api/projects/${projectId}/photos/${photoId}`, {
        headers,
      });
    },
    enabled: auth.isSignedIn && !!projectId && !!photoId,
  });
}

// Upload a photo
export function useUploadPhoto(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, metadata }: UploadPhotoData) => {
      const headers: Record<string, string> = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      // Remove Content-Type header - browser will set it for FormData
      delete headers["Content-Type"];

      const formData = new FormData();
      formData.append("file", file);

      if (metadata) {
        formData.append("metadata", JSON.stringify(metadata));
      }

      const apiUrl = import.meta.env.VITE_API_URL || "";
      const response = await fetch(`${apiUrl}/api/projects/${projectId}/photos`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to upload photo");
      }

      return response.json() as Promise<PhotoResponse>;
    },
    onSuccess: (_, variables) => {
      // Invalidate both the project photos and specific daily log photos
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "photos"] });
      if (variables.metadata?.dailyLogId) {
        queryClient.invalidateQueries({
          queryKey: ["projects", projectId, "photos", variables.metadata.dailyLogId],
        });
      }
    },
  });
}

// Update photo metadata
export function useUpdatePhoto(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePhotoData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<PhotoResponse>(`/api/projects/${projectId}/photos/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "photos"] });
      if (result.photo.dailyLogId) {
        queryClient.invalidateQueries({
          queryKey: ["projects", projectId, "photos", result.photo.dailyLogId],
        });
      }
    },
  });
}

// Delete a photo
export function useDeletePhoto(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const apiUrl = import.meta.env.VITE_API_URL || "";
      const response = await fetch(`${apiUrl}/api/projects/${projectId}/photos/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok && response.status !== 204) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete photo");
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "photos"] });
    },
  });
}
